import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
} from '@mui/material';
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Fingerprint as FingerprintIcon,
  School as SchoolIcon,
  Payments as PaymentsIcon,
  AdminPanelSettings as AdminIcon,
  FolderOpen as FolderOpenIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  getEmployees,
  getAttendanceRecords,
  getRequests,
  getBranches,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useKioskTerminalLauncher } from '../hooks/useKioskTerminalLauncher';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ pr: 1 }}>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const QuickLinkCard = ({ title, description, icon, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardActionArea onClick={onClick} sx={{ height: '100%', p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            pt: 0.25,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <ChevronRightIcon color="action" fontSize="small" />
      </Stack>
    </CardActionArea>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isManager } = useAuth();
  const { requestOpenTerminal, authDialog: kioskAuthDialog } = useKioskTerminalLauncher();
  const [stats, setStats] = useState({
    employees: 0,
    attendanceEmployees: 0,
    attendanceEntradas: 0,
    attendanceSalidas: 0,
    requests: 0,
    branches: 0,
  });

  const loadStats = useCallback(async () => {
    try {
      const todayStr = format(new Date(), 'yyyy-MM-dd');

      const [employeesRes, attendanceRes, requestsRes] = await Promise.all([
        getEmployees(),
        getAttendanceRecords({ startDate: todayStr, endDate: todayStr, limit: 5000 }),
        getRequests({ status: 'pending' }),
      ]);

      const rows = attendanceRes.data;
      const employeeIds = new Set(
        rows.map((r) => r.employee?._id || r.employee).filter(Boolean)
      );

      const newStats = {
        employees: employeesRes.data.length,
        attendanceEmployees: employeeIds.size,
        attendanceEntradas: rows.filter((r) => r.kind === 'entrada').length,
        attendanceSalidas: rows.filter((r) => r.kind === 'salida').length,
        requests: requestsRes.data.length,
        branches: 0,
      };

      if (isAdmin) {
        const branchesRes = await getBranches();
        newStats.branches = branchesRes.data.length;
      }

      setStats(newStats);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const branch = user?.branch;
  const branchName = typeof branch === 'object' ? branch?.name : null;

  const quickLinks = [
    {
      title: 'Asistencia',
      description: 'Entradas y salidas del personal',
      icon: <CheckCircleIcon />,
      onClick: () => navigate('/attendance'),
    },
    {
      title: 'Terminal tablet',
      description: 'Pantalla de entrada/salida para colaboradores (requiere tu contraseña)',
      icon: <FingerprintIcon />,
      onClick: requestOpenTerminal,
      roles: ['admin', 'manager'],
    },
    {
      title: 'Solicitudes',
      description:
        stats.requests > 0
          ? `${stats.requests} pendiente${stats.requests === 1 ? '' : 's'} por revisar`
          : 'Permisos, vacaciones y licencias',
      icon: <AssignmentIcon />,
      onClick: () => navigate('/requests'),
    },
    {
      title: 'Empleados',
      description: 'Expedientes, fotos y datos del personal',
      icon: <PeopleIcon />,
      onClick: () => navigate('/employees'),
    },
    {
      title: 'Capacitación',
      description: 'Cursos y avance por colaborador',
      icon: <SchoolIcon />,
      onClick: () => navigate('/training'),
    },
  ];

  if (isAdmin) {
    quickLinks.push(
      {
        title: 'Nómina',
        description: 'Cortes de pago, descuentos y reporte de gastos',
        icon: <PaymentsIcon />,
        onClick: () => navigate('/payroll'),
      },
      {
        title: 'Panel administración',
        description: 'Gráficas y reportes de toda la red',
        icon: <AdminIcon />,
        onClick: () => navigate('/admin'),
      },
      {
        title: 'Sucursales',
        description:
          stats.branches > 0
            ? `${stats.branches} ubicación${stats.branches === 1 ? '' : 'es'} activas`
            : 'Alta y datos de contacto',
        icon: <BusinessIcon />,
        onClick: () => navigate('/branches'),
      }
    );
  }

  const visibleQuickLinks = quickLinks.filter((link) => {
    if (!link.roles) return true;
    if (link.roles.includes('admin') && isAdmin) return true;
    if (link.roles.includes('manager') && isManager) return true;
    return false;
  });

  return (
    <Container maxWidth="lg">
      {kioskAuthDialog}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Bienvenido, {user?.name}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
          <Typography variant="body1" color="text.secondary">
            Resumen del día · {format(new Date(), "EEEE d 'de' MMMM", { locale: es })}
          </Typography>
          {isAdmin && (
            <Chip
              size="small"
              label={`${stats.branches} sucursales · ${stats.employees} empleados`}
              color="primary"
              variant="outlined"
            />
          )}
          {isManager && branchName && (
            <Chip size="small" label={branchName} color="primary" variant="outlined" />
          )}
        </Stack>
        {isManager && branch && typeof branch === 'object' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {[branch.address, branch.city].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Empleados"
            value={stats.employees}
            icon={<PeopleIcon fontSize="large" />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Asistencia hoy"
            value={stats.attendanceEmployees}
            subtitle={`${stats.attendanceEntradas} entradas · ${stats.attendanceSalidas} salidas registradas`}
            icon={<CheckCircleIcon fontSize="large" />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Solicitudes Pendientes"
            value={stats.requests}
            icon={<AssignmentIcon fontSize="large" />}
            color="#ed6c02"
          />
        </Grid>
        {isAdmin && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Sucursales"
              value={stats.branches}
              icon={<BusinessIcon fontSize="large" />}
              color="#9c27b0"
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <FolderOpenIcon color="primary" />
            <Typography variant="h6">Accesos rápidos</Typography>
          </Stack>
          <Grid container spacing={2}>
            {visibleQuickLinks.map((link) => (
              <Grid item xs={12} sm={6} md={4} key={link.title}>
                <QuickLinkCard
                  title={link.title}
                  description={link.description}
                  icon={link.icon}
                  onClick={link.onClick}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;
